<template lang="pug">
  yz-card(v-bind="cardProps" outlined :class="cardClasses" :style="hoverStyles" @click="handleCardClick" tabindex="-1")
    .yz-checkbox-selection-card__content(:class="contentClasses")
      .yz-checkbox-selection-card__main
        yz-checkbox.yz-checkbox-selection-card__checkbox(ref="checkbox" :value="value" :checked="isSelected" :disabled="disabled" :invalid="computedInvalid" @change="handleChange" @focus="onFocus" @blur="onBlur")
        .yz-checkbox-selection-card__text
          .yz-checkbox-selection-card__label(v-if="label || $slots.label")
            slot(name="label") {{ label }}
          .yz-checkbox-selection-card__description(v-if="description || $slots.description")
            slot(name="description") {{ description }}
</template>

<script>
import {getParentClasses, hasPaddingClasses} from '@/utils'

export default {
  name: 'YzCheckboxSelectionCard',
  inject: {
    checkboxGroup: {
      from: 'checkboxGroup',
      default: null
    }
  },
  props: {
    value: {
      type: [String, Number, Boolean, Object],
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    align: {
      type: String,
      default: 'start',
      validator: value => ['start', 'center', 'end'].includes(value)
    },
    radius: {
      type: [String, Number],
      default: '10px'
    },
    color: {
      type: String,
      default: 'white'
    },
    borderColor: {
      type: String,
      default: 'neutral-300'
    },
    selectedColor: {
      type: String,
      default: 'neutral-200'
    },
    selectedBorderColor: {
      type: String,
      default: 'neutral-700'
    },
    borderWidth: {
      type: [String, Number],
      default: 1
    },
    selectedBorderWidth: {
      type: [String, Number],
      default: 2
    },
    borderStyle: {
      type: String,
      default: 'solid'
    },
    hoverBackgroundColor: {
      type: String,
      default: 'neutral-200'
    },
    hoverBorderColor: {
      type: String,
      default: 'neutral-500'
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isSelected() {
      if (this.checkboxGroup) {
        return Array.isArray(this.checkboxGroup.value) && this.checkboxGroup.value.includes(this.value)
      }
      return false
    },
    computedInvalid() {
      return this.invalid || (this.checkboxGroup && this.checkboxGroup.hasError)
    },
    cardProps() {
      const props = {
        radius: this.radius,
        borderColor: (this.isSelected && !this.disabled) ? this.selectedBorderColor : this.borderColor,
        borderWidth: (this.isSelected && !this.disabled) ? this.selectedBorderWidth : this.borderWidth,
        borderStyle: this.borderStyle
      }
      
      // Apply selected color or default color to the card
      if (this.isSelected && !this.disabled && this.selectedColor) {
        // Use selectedColor when selected and not disabled
        props.color = this.selectedColor
      } else {
        // Use default color prop
        props.color = this.color
      }
      
      return props
    },
    cardClasses() {
      return {
        'yz-checkbox-selection-card': true,
        'yz-checkbox-selection-card--selected': this.isSelected && !this.disabled,
        'yz-checkbox-selection-card--disabled': this.disabled,
        'yz-checkbox-selection-card--invalid': this.computedInvalid
      }
    },
    contentClasses() {
      const parentClass = getParentClasses(this.$vnode)

      const classes = {
        'yz-checkbox-selection-card__content--selected': this.isSelected && !this.disabled,
        [`yz-checkbox-selection-card__content--align-${this.align}`]: true
      }

      // Apply smart padding logic
      if (hasPaddingClasses(parentClass)) {
        // If parent has padding classes, use them
        classes[parentClass] = true
      } else {
        // Default padding if no parent padding classes
        classes['pa-4'] = true
      }

      return classes
    },
    hoverStyles() {
      const styles = {}
      
      // Handle hover background color
      if (this.hoverBackgroundColor.startsWith('#')) {
        styles['--hover-bg-color'] = this.hoverBackgroundColor
      } else if (this.hoverBackgroundColor.startsWith('var(')) {
        styles['--hover-bg-color'] = this.hoverBackgroundColor
      } else if (this.hoverBackgroundColor.startsWith('yz-')) {
        styles['--hover-bg-color'] = `var(--${this.hoverBackgroundColor})`
      } else {
        styles['--hover-bg-color'] = `var(--yz-${this.hoverBackgroundColor})`
      }
      
      // Handle hover border color
      if (this.hoverBorderColor.startsWith('#')) {
        styles['--hover-border-color'] = this.hoverBorderColor
      } else if (this.hoverBorderColor.startsWith('var(')) {
        styles['--hover-border-color'] = this.hoverBorderColor
      } else if (this.hoverBorderColor.startsWith('yz-')) {
        styles['--hover-border-color'] = `var(--${this.hoverBorderColor})`
      } else {
        styles['--hover-border-color'] = `var(--yz-${this.hoverBorderColor})`
      }
      
      return styles
    }
  },
  methods: {
    handleCardClick() {
      if (!this.disabled) {
        this.handleChange()
      }
    },
    handleChange() {
      if (!this.disabled) {
        if (this.checkboxGroup) {
          // Toggle the checkbox state - if currently selected, unselect it, otherwise select it
          const newCheckedState = !this.isSelected
          this.checkboxGroup.updateValue(this.value, newCheckedState)
        } else {
          // For standalone use, emit the value and new checked state
          const newCheckedState = !this.isSelected
          this.$emit('input', this.value)
          this.$emit('change', this.value, newCheckedState)
        }
      }
    },
    onFocus(event) {
      this.$emit('focus', event)
    },
    onBlur(event) {
      this.$emit('blur', event)
    },
    focus() {
      this.$refs.checkbox.focus()
    },
    blur() {
      this.$refs.checkbox.blur()
    }
  }
}
</script>

<style lang="scss" scoped>
.yz-checkbox-selection-card {
  cursor: pointer;
  position: relative;

  // Separate transitions for different properties to avoid flicker
  transition: box-shadow 0.2s ease;

  // Prevent v-card focus/active states from interfering
  &:focus,
  &:active,
  &.v-card--link:focus,
  &.v-card--link:active {
    outline: none !important;
    box-shadow: none !important;
  }

  // Override any v-card focus background changes
  &:focus::before,
  &:active::before,
  &.v-card--link:focus::before,
  &.v-card--link:active::before {
    opacity: 0 !important;
  }

  // The card itself should have stable border without transition
  &:hover:not(.yz-checkbox-selection-card--disabled):not(.yz-checkbox-selection-card--selected) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    // Apply hover background color if defined
    background-color: var(--hover-bg-color) !important;
    
    // Apply hover border color if defined
    border-color: var(--hover-border-color) !important;
  }

  // Trigger checkbox button hover state when hovering the card
  &:hover:not(.yz-checkbox-selection-card--disabled) {
    // Target the yz-checkbox component's button to show hover state
    ::v-deep .yz-checkbox__box {
      border-color: var(--yz-neutral-500);
    }
  }

  &__content {
    position: relative;
    border-radius: inherit;
    transition: background-color 0.15s ease;

    // Alignment options
    &--align-start {
      .yz-checkbox-selection-card__main {
        align-items: flex-start;
      }
    }

    &--align-center {
      .yz-checkbox-selection-card__main {
        align-items: center;
      }
    }

    &--align-end {
      .yz-checkbox-selection-card__main {
        align-items: flex-end;
      }
    }

    // Selected background color is now handled via dynamic style binding
  }

  &__main {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__checkbox {
    flex-shrink: 0;
    margin-top: 2px; // Small offset to align with text baseline
  }

  &__text {
    flex: 1;
    min-width: 0; // Allow text to shrink
  }

  &__label {
    color: var(--yz-neutral-700);
    font-weight: 500;
    margin-bottom: 4px;
    transition: color 0.15s ease;
  }

  &__description {
    color: var(--yz-neutral-500);
    font-size: 14px;
    line-height: 1.4;
  }

  &--selected {
    .yz-checkbox-selection-card__label {
      color: var(--yz-neutral-700);
    }
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.6;
    border-color: transparent;

    &:hover {
      box-shadow: none;
    }
  }

  &--invalid {
    // Override card border color with error color
    border-color: var(--yz-danger) !important;
    
    // Error background for better visibility
    background-color: var(--yz-danger-50) !important;
    
    // Override hover states when invalid
    &:hover:not(.yz-checkbox-selection-card--disabled) {
      border-color: var(--yz-danger) !important;
      background-color: var(--yz-danger-100) !important;
      box-shadow: 0 2px 8px rgba(220, 53, 69, 0.15);
    }
    
    // Override selected states when invalid  
    &.yz-checkbox-selection-card--selected {
      border-color: var(--yz-danger) !important;
      background-color: var(--yz-danger-100) !important;
    }
    
    // Error styling for label text
    .yz-checkbox-selection-card__label {
      color: var(--yz-danger);
    }
  }
}
</style>